// @flow
import React from 'react';
import { DogIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { HouseholdSection } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  Banner,
  Box,
  Button,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AddIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { formatDateDisplayUTC } from '@fortress-technology-solutions/fortress-component-library/utils/index';

import useIntl from '../../../hooks/useIntl';
import appMessages from '../../App/messages';
import DashesIfNullOrUndefined from '../../../components/DashesIfNullOrUndefined';
import ServiceAnimalIndicator from '../ServiceAnimalIndicator';
import PetModal from './PetModal';
import usePetsSection from './hooks/usePetsSection';
import messages from './messages';
import type { PetsSectionProps } from './types';

const PetsSection = ({
  organizationId,
  propertyId,
  residentId,
  applicationId = null,
  applicants = null,
  canEdit,
  canDelete = false,
  hasOpenCerts = false,
  onSaveSuccess = null,
}: PetsSectionProps) => {
  const intl = useIntl();
  const isApplicantSubject =
    !residentId && Boolean(applicationId) && Boolean(applicants);
  const {
    pets,
    isLoading,
    isError,
    selectedPet,
    isModalOpen,
    handleAddPet,
    handleSelectPet,
    handleCloseModal,
    isPetClickable,
  } = usePetsSection({
    organizationId,
    propertyId,
    residentId,
    applicants,
    isApplicantSubject,
    canEdit,
  });

  if (!residentId && !isApplicantSubject) return null;

  const rows =
    isLoading || isError
      ? []
      : pets.map((pet) => {
          // Do not mute applicant rows.
          const color =
            isApplicantSubject || pet.kind === 'resident'
              ? null
              : 'text.secondary';
          const nameColor = pet.kind === 'prior' ? 'primary.light' : color;
          const clickable = isPetClickable(pet);

          return (
            <TableRow
              key={pet.id}
              sx={{ color, backgroundColor: 'transparent !important' }}
            >
              <TableCell>
                {clickable ? (
                  // Apply `nameColor` directly because anchors do not inherit it.
                  <Box
                    component="a"
                    onClick={() => handleSelectPet(pet)}
                    sx={{ color: nameColor, cursor: 'pointer' }}
                  >
                    <DashesIfNullOrUndefined data={pet.name} />
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: nameColor }}>
                    <DashesIfNullOrUndefined data={pet.name} />
                  </Box>
                )}
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <span>
                    <DashesIfNullOrUndefined data={pet.petTypeName} />
                  </span>
                  {/* This section always identifies service animals. */}
                  {pet.isServiceAnimal && (
                    <ServiceAnimalIndicator alwaysVisible />
                  )}
                </Stack>
              </TableCell>
              {/* Applicant pets have no household status or dates. */}
              {!isApplicantSubject && (
                <>
                  <TableCell>{pet.status}</TableCell>
                  <TableCell>
                    {pet.startDate && formatDateDisplayUTC(pet.startDate)}
                  </TableCell>
                  <TableCell>
                    {pet.endDate && formatDateDisplayUTC(pet.endDate)}
                  </TableCell>
                </>
              )}
            </TableRow>
          );
        });

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        {canEdit && hasOpenCerts && (
          /*
           * HouseholdSection renders its own Add from `buttonProps` with
           * nothing wrapping it, and a disabled button receives no pointer
           * events -- so a tooltip hung on that button never fires on hover.
           * The library's Tooltip injects a hoverable container around its
           * child, which is how the card's Add-household-member button shows
           * this same message while disabled. Getting that container means
           * owning the button, so the locked Add is rendered here and the
           * section's own slot is left empty.
           */
          <Box sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>
            <Tooltip
              placement="top"
              title={intl.formatMessage(appMessages.editHouseholdBlocked)}
            >
              <Button variant="text" disabled data-testid="add-pet">
                <AddIcon sx={{ marginRight: 1 }} />
                Add
              </Button>
            </Tooltip>
          </Box>
        )}
        <HouseholdSection
          intl={intl}
          Icon={DogIcon}
          title={intl.formatMessage(messages.pets)}
          // An open certification freezes the household composition, so a pet
          // cannot be added until it is completed or cancelled -- Add stays
          // visible but inert, and says why. Existing pets stay openable
          // read-only.
          buttonProps={
            canEdit && !hasOpenCerts ? { onClick: handleAddPet } : undefined
          }
          headers={[
            messages.name,
            messages.petType,
            ...(isApplicantSubject
              ? []
              : [messages.status, messages.startDate, messages.endDate]),
          ]}
          rows={rows}
          // Distinguish loading and errors from an empty result.
          EmptyStateComponent={
            <AsyncBox loading={isLoading}>
              {isError ? (
                <Banner
                  status="error"
                  message={intl.formatMessage(messages.petsLoadError)}
                  sx={{ width: '100%' }}
                />
              ) : (
                <Box>{intl.formatMessage(messages.noPets)}</Box>
              )}
            </AsyncBox>
          }
        />
      </Box>
      {isModalOpen && (
        <PetModal
          // Remount for each selection because `useForm` reads defaults once.
          key={selectedPet?.id ?? 'new-pet'}
          organizationId={organizationId}
          propertyId={propertyId}
          residentId={residentId}
          applicationId={applicationId}
          applicants={applicants}
          isApplicantSubject={isApplicantSubject}
          pet={selectedPet}
          hasOpenCerts={hasOpenCerts}
          canDelete={canDelete}
          onClose={handleCloseModal}
          onSaveSuccess={onSaveSuccess}
        />
      )}
    </>
  );
};

export default PetsSection;
